import { toMinutes } from "@/lib/schedule.js";
import { formatClock } from "@/lib/schedule.js";
import { cx } from "@/lib/cx.js";

/*
 * The shift on a fixed 24-HOUR axis, from schedules.html.
 *
 * Anchored to the clock rather than to the shift, so every row is on the same
 * scale and two schedules can be compared at a glance — a 09:00 start sits left
 * of a 22:00 one. A shift-relative bar would make them all look identical.
 *
 * An overnight shift is drawn as TWO segments, 22:00→24:00 and 00:00→06:00,
 * which is what crossing midnight actually looks like on a day.
 */
const DAY = 24 * 60;
const pct = (minutes) => (minutes / DAY) * 100;

/*
 * No min-width here, deliberately. The markup's version carries `min-w-48`, and
 * inside the row's flex layout that makes the bar wider than the box it was
 * given — it then spills right and sits ON TOP of the shift times, hiding the
 * leading digits ("10:00 PM" reads as "00 PM"). The flex parent already gives it
 * whatever room is left.
 */
export default function ShiftTimeline({ startTime, endTime, breakTimes = [], compact, className }) {
    const start = toMinutes(startTime);
    const end = toMinutes(endTime);
    if (start === null || end === null) return null;

    // end > start is one block; otherwise it wraps around midnight.
    const segments = end > start ? [[start, end]] : [[start, DAY], [0, end]];

    return (
        <div className={cx("w-full", className)}>
            <div
                className={cx(
                    "relative w-full overflow-hidden rounded bg-slate-100 dark:bg-slate-800",
                    compact ? "h-3" : "h-4",
                )}
            >
                {segments.map(([from, to], i) => (
                    <span
                        key={`w${i}`}
                        data-slot={`${Math.round(from)}-${Math.round(to)}`}
                        className="absolute top-0 bottom-0 rounded bg-brand-500/85"
                        style={{ left: `${pct(from)}%`, width: `${pct(to - from)}%` }}
                    />
                ))}

                {breakTimes.map((br, i) => {
                    const from = toMinutes(br.startTime);
                    const to = toMinutes(br.endTime);
                    if (from === null || to === null) return null;

                    const width = to > from ? to - from : DAY - from + to;
                    return (
                        <span
                            key={`b${i}`}
                            data-break={br.name}
                            title={`${br.name} ${formatClock(br.startTime)}–${formatClock(br.endTime)}${br.isPaid ? " · paid" : ""}`}
                            className={cx(
                                "absolute top-0 bottom-0 rounded",
                                br.isPaid
                                    ? "bg-emerald-400"
                                    : "bg-white ring-1 ring-brand-500/40 dark:bg-slate-900",
                            )}
                            style={{ left: `${pct(from)}%`, width: `${pct(width)}%` }}
                        />
                    );
                })}
            </div>

            {!compact && (
                <div className="relative mt-1 h-3">
                    {[0, 6, 12, 18, 24].map((h) => (
                        <span
                            key={h}
                            className="absolute -translate-x-1/2 font-mono text-[9.5px] text-slate-400 dark:text-slate-500"
                            style={{ left: `${(h / 24) * 100}%` }}
                        >
                            {String(h).padStart(2, "0")}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
